import { add, isNaN, map } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { parseNumber } from '../../../utils/number-util';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import * as actions from '../actions';
import {
  selectCategories,
  selectPurchaseOrderInEdit,
  selectVendors,
  selectSubTotal,
  selectAdditionalCost,
} from '../purchaseOrderReducer';
import PurchaseOrderForm from './PurchaseOrderForm';

const PurchaseOrderFormWrapper = forwardRef(function PurchaseOrderFormWrapper(props, ref) {
  const calculateWithDiscount = (cost, discount) => {
    const costWithDiscount = cost - (parseNumber(discount) || 0);
    if (costWithDiscount < 0) {
      return 0;
    }

    return costWithDiscount;
  };

  const stableDefaults = React.useMemo(
    () => cloneDeep(props.initialValues || {}),
    [props.initialValues]
  );
  const methods = useForm({ defaultValues: stableDefaults });
  const { handleSubmit, control } = methods;
  const watchAll = useWatch({ control });

  useEffect(() => {
    props.fetchEmployees();
    props.fetchPurchaseOrderCategories();
    props.fetchPurchaseOrderVendors();
  }, []);

  // compute and dispatch subtotal when items change
  useEffect(() => {
    const fields = (watchAll && watchAll.items) || [];
    const addedAmount = map(fields, (field) => {
      if (!field || isNaN(field.quantity * parseNumber(field.price || ''))) {
        return 0;
      }
      return field.quantity * parseNumber(field.price);
    });
    props.setSubTotal(addedAmount);
  }, [watchAll && watchAll.items]);

  // compute and dispatch additional cost when tax/shipping change
  useEffect(() => {
    const taxAmount = (watchAll && watchAll.taxAmount) || '0';
    const shippingAmount = (watchAll && watchAll.shippingAmount) || '0';
    const additionalCost = add(parseNumber(taxAmount), parseNumber(shippingAmount));
    props.setAdditionalCost(additionalCost);
  }, [watchAll && watchAll.taxAmount, watchAll && watchAll.shippingAmount]);

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(props.onSubmit)(),
  }));

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(props.onSubmit)}>
        <PurchaseOrderForm {...props} calculateWithDiscount={calculateWithDiscount} />
      </form>
    </FormProvider>
  );
});

PurchaseOrderFormWrapper.defaultProps = {
  categories: [],
  vendors: [],
  shippingAmount: '0',
  taxAmount: '0',
  discount: '0',
};

PurchaseOrderFormWrapper.propTypes = {
  fetchPurchaseOrderVendors: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  vendors: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  taxAmount: PropTypes.string,
  shippingAmount: PropTypes.string,
  discount: PropTypes.string,
  // eslint-disable-next-line
  initialValues: PropTypes.shape({}),
  isNew: PropTypes.bool, // eslint-disable-line
};

const initialFormValues = () => ({
  orderDate: moment().format('YYYY-MM-DD'),
  trackingNumber: '',
  memo: '',
});

const mapStateToProps = (state, ownProps) => {
  const inEdit = selectPurchaseOrderInEdit(state);
  const initialValues = ownProps.isNew
    ? initialFormValues(state)
    : inEdit
    ? {
        ...inEdit,
        items: (inEdit.items || []).map((item) => ({ ...item })),
      }
    : ownProps.initialValues;
  return {
    categories: selectCategories(state),
    vendors: selectVendors(state),
    employees: selectEmployees(state),
    initialValues,
    itemCosts: selectSubTotal(state),
    additionalCost: selectAdditionalCost(state),
    discount: state.purchaseOrders.formTotals.discount || '0',
  };
};

const mapStateToDispatch = (dispatch) =>
  bindActionCreators(
    {
      fetchPurchaseOrderCategories: actions.fetchPurchaseOrderCategories,
      fetchPurchaseOrderVendors: actions.fetchPurchaseOrderVendors,
      setSubTotal: actions.setSubTotal,
      setAdditionalCost: actions.setAdditionalCost,
      fetchEmployees,
    },
    dispatch
  );

const enhance = compose(connect(mapStateToProps, mapStateToDispatch, null, { forwardRef: true }));

export default enhance(PurchaseOrderFormWrapper);
